import { StatusCodes } from 'http-status-codes';
import { useCallback, useContext, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import {
  FormState,
  UseFormGetValues,
  UseFormReset,
} from 'react-hook-form/dist/types/form';
import { useNavigate } from 'react-router-dom';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { EmailPurchaseOrderContext } from 'src/contexts/EmailPurchaseOrderContext/EmailPurchaseOrderContext';
import { ModalContext } from 'src/contexts/ModalContext/ModalContext';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { isInPurchasesView } from 'src/utils/deepLinkUtils';
import { hasChanged } from 'src/utils/posFieldUtils';
import {
  getPurchaseOrderDetailsModalConfigWithDeepLink,
  getPurchaseOrderRelativeUrl,
  PurchaseOrderDetailsInput,
} from 'src/utils/purchaseUtils';
import { tagFieldHasChanges } from 'src/utils/tagUtils';
import {
  ActionOutboxEntityType,
  AutoPoClient,
  PosFieldArrayOfTag,
  PurchaseClient,
  PurchaseOrderDetails,
  PurchaseOrderTicketGroup,
  TicketClient,
  TicketGroupEditReason,
  TicketGroupMergeInput,
} from 'src/WebApiController/index';

import { BatchedPurchaseOrderCreator } from './BatchedPurchaseOrderCreator';

export const usePurchaseOrderOperations = (
  getValues: UseFormGetValues<PurchaseOrderDetailsInput>,
  formState: FormState<PurchaseOrderDetailsInput>,
  reset: UseFormReset<PurchaseOrderDetailsInput>,
  highlightTicketGroupIds?: number[],
  hasStandaloneEntityDetailModalsFeature?: boolean,
  hasCreatePOTicketGroupsInChunkFeature?: boolean
) => {
  const { showErrorDialog } = useClientLogContext();
  const { setModal } = useContext(ModalContext);
  const navigate = useNavigate();
  const { activeAccountWebClientConfig, mainRoute } = useAppContext();

  const { inboundEmailId, classificationType } = useContext(
    EmailPurchaseOrderContext
  );
  const { refetch: refreshTagMetadata, tagsMetadata } = useTagsForEntityType(
    ActionOutboxEntityType.Purchase
  );

  const { setActivePosEntity } =
    useActivePosEntityContext<PurchaseOrderDetails>(
      ActionOutboxEntityType.Purchase
    );

  const {
    updateItemInEvent,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const purchaseOrderCreator = useMemo(() => {
    const client = new PurchaseClient(activeAccountWebClientConfig);
    return new BatchedPurchaseOrderCreator(
      client,
      hasCreatePOTicketGroupsInChunkFeature
    );
  }, [activeAccountWebClientConfig, hasCreatePOTicketGroupsInChunkFeature]);

  const onCreatePurchaseOrder = useCallback(
    async (purchaseDetailsForm: PurchaseOrderDetailsInput) => {
      const result = await purchaseOrderCreator.create(purchaseDetailsForm);
      if (result?.entityId == null) {
        let errorMessage = 'Failed to insert purchase order';

        if (result?.message != null) {
          errorMessage += `: ${result.message}`;
        }

        showErrorDialog(
          'PurchaseClient.insertPurchaseOrder',
          {
            message: errorMessage,
            status: result?.status ?? StatusCodes.INTERNAL_SERVER_ERROR,
          },
          { trackErrorData: purchaseDetailsForm }
        );
      } else {
        const savePurchaseOrderId = result.entityId;

        if (inboundEmailId != null) {
          new AutoPoClient(activeAccountWebClientConfig).classifyInboundEmail(
            inboundEmailId,
            classificationType,
            savePurchaseOrderId,
            null
          );

          navigate(
            `${getPurchaseOrderRelativeUrl(savePurchaseOrderId)}&fromAutoPo=true`
          );
        } else if (setActivePosEntity != null) {
          // Refresh the active data so the SaleDetail dialog and table will have the new content
          setActivePosEntity(
            savePurchaseOrderId,
            null,
            true,
            undefined,
            undefined,
            undefined,
            ActionOutboxEntityType.Purchase
          );

          // Avoid calling navigate(getPurchaseOrderRelativeUrl()) because
          // the modal is used outside the purchases, and this will make
          // a non-wanted navigation.
          setModal(
            getPurchaseOrderDetailsModalConfigWithDeepLink(savePurchaseOrderId)
          );

          if (
            hasStandaloneEntityDetailModalsFeature &&
            !isInPurchasesView(mainRoute)
          ) {
            // Refresh the expanded lists after updating purchases in case they're on inventory/sales pages
            refreshExpandedListItems?.();
          }
        }
      }
    },
    [
      activeAccountWebClientConfig,
      classificationType,
      hasStandaloneEntityDetailModalsFeature,
      inboundEmailId,
      mainRoute,
      navigate,
      purchaseOrderCreator,
      refreshExpandedListItems,
      setActivePosEntity,
      setModal,
      showErrorDialog,
    ]
  );

  const getUpdateEligibility = useCallback(
    (form: PurchaseOrderDetailsInput) => {
      const defaultValues = formState.defaultValues;
      const ticketGroupsChanged = !isEqual(
        form.ticketGroups,
        defaultValues?.ticketGroups
      );
      const costsChanged = !isEqual(
        form.additionalCosts,
        defaultValues?.additionalCosts
      );
      const paymentsChanged = !isEqual(form.payments, defaultValues?.payments);

      const dealChanged = !isEqual(form.deal, formState.defaultValues?.deal);

      const eligibleToUpdatePurchaseOrder =
        ticketGroupsChanged ||
        costsChanged ||
        paymentsChanged ||
        dealChanged ||
        hasChanged(form.currencyCode, defaultValues?.currencyCode) ||
        hasChanged(form.buyerUserId, defaultValues?.buyerUserId) ||
        hasChanged(form.purchaseDate, defaultValues?.purchaseDate) ||
        hasChanged(form.vendor, defaultValues?.vendor) ||
        hasChanged(form.vendorAccount, defaultValues?.vendorAccount) ||
        hasChanged(form.vendorOrderId, defaultValues?.vendorOrderId) ||
        hasChanged(
          form.secondaryVendorAccount,
          defaultValues?.secondaryVendorAccount
        ) ||
        hasChanged(form.transferId, defaultValues?.transferId) ||
        tagFieldHasChanges(
          form.tags,
          (defaultValues?.tags ?? {}) as PosFieldArrayOfTag,
          tagsMetadata ?? []
        ) ||
        hasChanged(form.privateNotes, defaultValues?.privateNotes) ||
        hasChanged(form?.commissions, defaultValues?.commissions) ||
        hasChanged(form?.qcState, defaultValues?.qcState) ||
        hasChanged(
          form?.isCommissionsLocked,
          defaultValues?.isCommissionsLocked
        );

      const eligibleToUpdateMergeTicketGroups =
        ticketGroupsChanged || paymentsChanged || costsChanged;

      return {
        eligibleToUpdatePurchaseOrder,
        eligibleToUpdateMergeTicketGroups,
        paymentsOrCostsChanged: paymentsChanged || costsChanged,
      };
    },
    [formState.defaultValues, tagsMetadata]
  );

  const getMergeIntentions = useCallback(
    (paymentsOrCostsChanged: boolean) => {
      const tgIntentionsMap = getValues('ticketGroupIntentions');
      if (paymentsOrCostsChanged) {
        getValues('ticketGroups').forEach(({ ticketGroupId }) => {
          if (!tgIntentionsMap[ticketGroupId]?.reason) {
            tgIntentionsMap[ticketGroupId] = {
              ticketGroupId: ticketGroupId,
              reason: TicketGroupEditReason.AmendTickets,
              additionalInfo: 'Update payment and cost',
            };
          }
        });
      }

      return Object.values(tgIntentionsMap).map((tgi) => {
        // New ad groups (negative ids) can be edited/ after being added, so we want to change it back to add
        // so the backend can handle it properly
        // We don't care about the Cancel intention because it will result in no-op
        if (
          tgi.ticketGroupId < 0 &&
          tgi.reason === TicketGroupEditReason.AmendTickets
        ) {
          tgi.reason = TicketGroupEditReason.AddTickets;
        }

        // In MultiTicketGroup world, user can still edit Tgs in "Add Tickets" mode, so we need to convert Add->Amend here if the user edited an existing TG
        if (
          tgi.ticketGroupId > 0 &&
          tgi.reason === TicketGroupEditReason.AddTickets
        ) {
          tgi.reason = TicketGroupEditReason.AmendTickets;
        }

        return tgi;
      });
    },
    [getValues]
  );

  const onUpdatePurchaseOrder = useCallback(
    async (purchaseDetailsForm: PurchaseOrderDetailsInput) => {
      let reloadWizard = false;

      const {
        eligibleToUpdatePurchaseOrder,
        eligibleToUpdateMergeTicketGroups,
        paymentsOrCostsChanged,
      } = getUpdateEligibility(purchaseDetailsForm);

      // If we have any change from ticket-groups or costs, that could affect the PO main props (total-cost, unit cost, num of tickets and ticket-groups, etc.)
      // Or if we made any changes explicitly on the PO, then call updatePurchaseOrder to update those first-class props
      if (eligibleToUpdatePurchaseOrder) {
        reloadWizard = true;
        const result = await new PurchaseClient(
          activeAccountWebClientConfig
        ).updatePurchaseOrderByPurchaseOrderId(
          purchaseDetailsForm.id,
          purchaseDetailsForm
        );

        if (result.status !== 200) {
          let errorMessage = 'Failed to update purchase order';

          if (result.message != null) {
            errorMessage += `: ${result.message}`;
          }

          showErrorDialog(
            'PurchaseClient.updatePurchaseOrder',
            { message: errorMessage, status: result.status },
            {
              trackErrorData: {
                purchaseDetailsForm,
                result,
              },
            }
          );
          return;
        }
      }

      if (eligibleToUpdateMergeTicketGroups) {
        reloadWizard = true;
        const intentions = getMergeIntentions(paymentsOrCostsChanged);
        await new TicketClient(
          activeAccountWebClientConfig
        ).mergeTicketGroupsForPurchaseOrderId(
          purchaseDetailsForm.id,
          {
            purchaseOrderId: purchaseDetailsForm.id,
            intentions: intentions,
            ticketGroups: purchaseDetailsForm.ticketGroups,
            updateSalesOfListings: purchaseDetailsForm.updateSalesOfListings,
            updatePredeliveryETicketArtifacts:
              purchaseDetailsForm.updatePredeliveryETicketArtifacts,
          } as TicketGroupMergeInput,
          true // isManual
        );

        // If the ticket group is the same as the one we are editing, we need to update the context
        if (
          purchaseDetailsForm.id > 0 &&
          updateItemInEvent != null &&
          intentions.length === 1 &&
          highlightTicketGroupIds?.length === 1
        ) {
          if (highlightTicketGroupIds[0] === intentions[0].ticketGroupId) {
            const purchaseOrderDetails = await new PurchaseClient(
              activeAccountWebClientConfig
            ).getPurchaseOrderByPurchaseOrderId(purchaseDetailsForm.id, false);
            if (purchaseOrderDetails) {
              updateItemInEvent(
                {
                  ...purchaseOrderDetails,
                  ...purchaseOrderDetails.ticketGroups[0],
                  ticketGroupItems: [],
                  isTktGrp: true,
                  id: purchaseOrderDetails.id,
                  tgId: purchaseOrderDetails.ticketGroups[0].id,
                  isMultEv: false,
                  entityCatalogData: null,
                  faceValCst:
                    purchaseOrderDetails.ticketGroups[0].faceVal ?? null,
                } as PurchaseOrderTicketGroup,
                ActionOutboxEntityType.Purchase
              );
            }
          }
        }
      }

      if (reloadWizard) {
        if (
          hasStandaloneEntityDetailModalsFeature &&
          !isInPurchasesView(mainRoute)
        ) {
          // Refresh the expanded lists after updating purchases in case they're on inventory/sales pages
          refreshExpandedListItems?.();
        }
        // Refresh the active data so the PurchaseDetail dialog and table will have the new content
        setActivePosEntity(purchaseDetailsForm.id, null, true);
        reset(purchaseDetailsForm);
        // Update the tags metadata
        refreshTagMetadata();
      }
    },
    [
      activeAccountWebClientConfig,
      getMergeIntentions,
      getUpdateEligibility,
      hasStandaloneEntityDetailModalsFeature,
      highlightTicketGroupIds,
      mainRoute,
      refreshExpandedListItems,
      refreshTagMetadata,
      reset,
      setActivePosEntity,
      showErrorDialog,
      updateItemInEvent,
    ]
  );

  return {
    onCreatePurchaseOrder,
    onUpdatePurchaseOrder,
    purchaseOrderCreator,
  };
};
